import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';

import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';
import { AnalyticsService } from 'src/app/components/logging/analytics.service';
import { ComponentsService } from '../../services/components.service';
import {
  GeneratedLayoutData,
  GenerateLayoutResponse,
  LayoutAiApiService,
} from './services/layout-ai-api.service';

export type LayoutAiMessageType = 'default' | 'no_credits' | 'error';

export interface LayoutAiShowOptions {
  component: string;
  componentId: string;
  layoutHtml?: string;
  onGenerated: (layout: GeneratedLayoutData) => void;
}

interface LayoutAiGeneration {
  prompt: string;
  failure?: Exclude<LayoutAiMessageType, 'default'>;
}

const PLACEHOLDER_EXAMPLES: Record<string, string> = {
  calendar: 'a list of events with the time on the left',
  counter: 'large numbers inside circles with the labels below',
  date: 'the day of the week above the full date',
  rss: 'two columns of cards with the image on the left',
  table: 'a grid of profile cards with the photo on top',
  time: 'a large digital clock with small seconds',
  weather: 'the current temperature above a row of daily forecasts',
};

@Component({
  selector: 'template-component-layout-ai',
  templateUrl: './layout-ai.component.html',
  styleUrls: ['./layout-ai.component.scss'],
  standalone: false,
})
export class LayoutAiComponent implements OnInit, OnDestroy {
  static readonly TYPE = 'rise-layout-generate-ai';

  component: string;
  componentId: string;
  prompt = '';
  loading = false;
  messageType: LayoutAiMessageType = 'default';

  private layoutHtml: string | undefined;
  private onGenerated: ((layout: GeneratedLayoutData) => void) | null = null;
  // Generations keep running after the user leaves the page; a failure is
  // kept to be shown when the page is reopened.
  private generations = new Map<string, LayoutAiGeneration>();
  private destroyed = false;

  constructor(
    private elementRef: ElementRef,
    private componentsService: ComponentsService,
    private layoutAiApi: LayoutAiApiService,
    private companyStateService: CompanyStateService,
    private userStateService: UserStateService,
    private analyticsFactory: AnalyticsService,
  ) {}

  ngOnInit(): void {
    this.componentsService.registerDirective({
      type: LayoutAiComponent.TYPE,
      element: this.elementRef.nativeElement,
      usesBackTitleHeader: () => true,
      getLabel: () => this.title,
      show: (options: LayoutAiShowOptions) => {
        this.component = options.component;
        this.componentId = options.componentId;
        this.layoutHtml = options.layoutHtml;
        this.onGenerated = options.onGenerated;
        this.restoreGeneration();
      },
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
  }

  get title(): string {
    return this.layoutHtml ? 'Edit layout' : 'Generate layout';
  }

  get placeholder(): string {
    if (this.layoutHtml) {
      return 'Describe the layout changes you want.';
    }

    const prompt = 'Describe the layout you want.';
    const example = PLACEHOLDER_EXAMPLES[this.component];

    return example ? `${prompt} e.g. ${example}` : prompt;
  }

  canGenerate(): boolean {
    return !!this.prompt.trim() && !this.loading;
  }

  canAccessCompanySettings(): boolean {
    return (
      this.userStateService.isRiseAdmin() || this.userStateService.isUserAdmin()
    );
  }

  isErrorMessage(): boolean {
    return this.messageType === 'error';
  }

  async onGenerate(): Promise<void> {
    if (!this.canGenerate()) {
      return;
    }

    const { component, componentId, layoutHtml, onGenerated } = this;
    const generation: LayoutAiGeneration = { prompt: this.prompt };
    const tracking = { component, componentId, edit: !!layoutHtml };
    const startedAt = Date.now();

    this.generations.set(componentId, generation);
    this.loading = true;
    this.messageType = 'default';

    try {
      const response = await this.layoutAiApi.generateLayout({
        component,
        prompt: generation.prompt.trim(),
        layoutHtml,
      });

      if (response.type === 'no_credits') {
        generation.failure = 'no_credits';
        this.trackGeneration('no_credits', tracking, startedAt, response);
      } else {
        this.trackGeneration('success', tracking, startedAt, response);

        if (!this.destroyed) {
          onGenerated?.(response.data);
        }
      }
    } catch {
      generation.failure = 'error';
      this.trackGeneration('error', tracking, startedAt);
    }

    if (!this.destroyed) {
      this.finishGeneration(componentId, generation);
    }
  }

  private restoreGeneration(): void {
    const generation = this.generations.get(this.componentId);

    this.prompt = generation?.prompt ?? '';
    this.loading = !!generation && !generation.failure;
    this.messageType = generation?.failure ?? 'default';

    if (generation?.failure) {
      this.generations.delete(this.componentId);
    }
  }

  private finishGeneration(
    componentId: string,
    generation: LayoutAiGeneration,
  ): void {
    if (!this.isShowing(componentId)) {
      if (!generation.failure) {
        this.generations.delete(componentId);
      }
      return;
    }

    this.generations.delete(componentId);
    this.loading = false;
    this.messageType = generation.failure ?? 'default';

    if (!generation.failure) {
      this.componentsService.onBackButton();
    }
  }

  // Selecting another component in the preview leaves the page without the back
  // button, so also check that the page is still the one being edited.
  private isShowing(componentId: string): boolean {
    return (
      this.componentsService.selected?.type === LayoutAiComponent.TYPE &&
      this.componentId === componentId
    );
  }

  private trackGeneration(
    status: 'success' | 'no_credits' | 'error',
    tracking: { component: string; componentId: string; edit: boolean },
    startedAt: number,
    response?: GenerateLayoutResponse,
  ): void {
    this.analyticsFactory.track('AI Layout Generate', {
      status,
      ...tracking,
      companyId: this.companyStateService.getSelectedCompanyId(),
      creditsUsed: response?.creditsUsed,
      durationMs: Date.now() - startedAt,
    });
  }
}
